/**
 * Admin OAuth Provider RPC definitions
 *
 * Admin endpoints for OAuth provider management — requires admin role.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, Unauthorized } from "../errors.ts";

// ============================================================================
// Request/Response Schemas
// ============================================================================

/**
 * Input for starting OAuth authorization flow
 * @since 1.0.0
 */
export const StartAuthInput = Schema.Struct({
  installationName: Schema.String,
});

/**
 * Response from starting OAuth authorization flow
 * @since 1.0.0
 */
export const StartAuthResponse = Schema.Struct({
  authUrl: Schema.String,
  state: Schema.String,
});

/**
 * Input for exchanging OAuth authorization code for tokens
 * @since 1.0.0
 */
export const ExchangeTokenInput = Schema.Struct({
  code: Schema.String,
  state: Schema.String,
  installationName: Schema.String,
});

/**
 * Response from token exchange
 * @since 1.0.0
 */
export const ExchangeTokenResponse = Schema.Struct({
  success: Schema.Boolean,
  provider: Schema.String,
  accountId: Schema.String,
});

/**
 * Input for checking OAuth connection status
 * @since 1.0.0
 */
export const CheckStatusInput = Schema.Struct({
  installationName: Schema.String,
});

/**
 * Response from status check
 * @since 1.0.0
 */
export const CheckStatusResponse = Schema.Struct({
  connected: Schema.Boolean,
  provider: Schema.String,
  expiresAt: Schema.NullOr(Schema.String),
});

/**
 * Input for starting the device-code authorization flow (RFC 8628).
 * @since 1.0.0
 */
export const StartDeviceAuthInput = Schema.Struct({
  installationName: Schema.String,
});

/**
 * Happy-path response from starting the device-code flow. `pollToken` is
 * the opaque Redis handle the browser polls with — the `device_auth_id`
 * secret stays server-side. `expiresAt` is when the user-code expires.
 *
 * @since 1.0.0
 */
export const StartDeviceAuthStarted = Schema.Struct({
  status: Schema.Literal("started"),
  userCode: Schema.String,
  verificationUri: Schema.String,
  intervalSeconds: Schema.Number,
  expiresAt: Schema.DateTimeUtc,
  pollToken: Schema.String,
});

/**
 * Response variant when the usercode step reports device login is switched
 * off on the account. A discriminated value — not a generic OAuthError — so
 * the web client branches into the guided enablement card (CLA-369).
 *
 * @since 1.0.0
 */
export const StartDeviceAuthDisabled = Schema.Struct({
  status: Schema.Literal("device_login_disabled"),
});

/**
 * Discriminated response from starting the device-code flow: either a
 * `started` session or the `device_login_disabled` guidance signal.
 *
 * @since 1.0.0
 */
export const StartDeviceAuthResponse = Schema.Union(StartDeviceAuthStarted, StartDeviceAuthDisabled);

/**
 * Input for polling a device-code authorization.
 * @since 1.0.0
 */
export const PollDeviceAuthInput = Schema.Struct({
  pollToken: Schema.String,
});

/**
 * Response from polling a device-code authorization. `pending` while the
 * user hasn't approved yet; `authorized` once tokens are persisted +
 * deployed; `expired` once the device-code window has lapsed (the Redis
 * poll-state entry was reaped or its `expires_at` passed) — a terminal
 * outcome the browser stops polling on.
 *
 * @since 1.0.0
 */
export const PollDeviceAuthResponse = Schema.Struct({
  status: Schema.Literal("pending", "authorized", "expired"),
  connected: Schema.Boolean,
});

/**
 * RPC-layer error for OAuth operations
 * @since 1.0.0
 */
export class OAuthRpcError extends Schema.TaggedError<OAuthRpcError>()(
  "OAuthRpcError",
  { message: Schema.String },
) {}

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin OAuth Provider RPC group
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminOAuthProviderRpcs extends RpcGroup.make(
  Rpc.make("admin.oauth.startAuth", {
    payload: StartAuthInput,
    success: StartAuthResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.oauth.exchangeToken", {
    payload: ExchangeTokenInput,
    success: ExchangeTokenResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.oauth.checkStatus", {
    payload: CheckStatusInput,
    success: CheckStatusResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.oauth.startDeviceAuth", {
    payload: StartDeviceAuthInput,
    success: StartDeviceAuthResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.oauth.pollDeviceAuth", {
    payload: PollDeviceAuthInput,
    success: PollDeviceAuthResponse,
    error: Schema.Union(OAuthRpcError, Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
